import { FloatingPortal, offset, size, useFloating, useDismiss, useInteractions } from "@floating-ui/react"
import { Transition } from "@headlessui/react"
import { useSSRDisabled } from "@skyltmax/ds9/hooks"
import cn from "classnames"
import React, { useRef, useEffect, useCallback, useState } from "react"
import { Trans, useTranslation } from "react-i18next"
import { type Sign as ExampleSign } from "@/api/graphql"
import Checkbox from "@/common/components/checkbox"
import Scrollable from "@/common/components/scrollable"
import useMedia from "@/common/hooks/use_media"
import { type SizeApplyArgs } from "@/common/util"
import { mergeRefs } from "@/common/util/react"
import { useMinimaticData, useMinimaticLoaded } from "@/signomatic/app/minimatic"
import type SignText from "@/signomatic/app/objects/sign_text"
import { type SignStateData } from "@/signomatic/app/sign"
import type Sign from "@/signomatic/app/sign"
import { arrayUniq } from "@/signomatic/util"
import Fonts from "@/signomatic/util/fonts"
import { isText } from "@/signomatic/util/types"
import { ArrowLeftLarge, ArrowRightLarge, LockClosedFill, CheckmarkLarge, ChevronDown, CloseLarge } from "@/skylticons"

const fontSeparator = " / "

interface FontPreview {
  width: number
  height: number
  data: string
}

const fontPreviews: Record<string, FontPreview | undefined> = {}

const computePreview = (name: string) => {
  const font = Fonts.getOpentypeFont(name, "normal")

  if (font) {
    const path = font.getPath(name, 0, 19, 19)
    const box = path.getBoundingBox()
    return {
      width: box.x2,
      height: box.y2,
      data: path.toPathData(2),
    }
  }

  return undefined
}

interface OptionProps {
  label: string
  selected: boolean
  onSelected: () => void
  scrollIntoView: boolean
  setScrolledIntoView: (value: boolean) => void
}

export const Option: React.FC<OptionProps> = ({ label, selected, onSelected, scrollIntoView, setScrolledIntoView }) => {
  const ref = useRef<HTMLLIElement>(null)

  useEffect(() => {
    if (scrollIntoView) {
      setTimeout(() => ref.current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "start" }), 1)
      setScrolledIntoView(true)
    }
  }, [scrollIntoView, setScrolledIntoView])

  const labels = label.split(fontSeparator)
  const previews: FontPreview[] = []

  labels.forEach(l => {
    let preview = fontPreviews[l]

    if (!preview) preview = fontPreviews[l] = computePreview(l)

    if (preview) previews.push(preview)
  })

  return (
    <li
      className={cn(
        `relative mb-0 flex h-12 w-full cursor-pointer scroll-mb-96 items-center border border-transparent pl-4 pr-12 text-base focus:border-primary lg:h-10 lg:scroll-m-28 font-${label?.replace(new RegExp(fontSeparator, "g"), "-")}`,
        {
          "hover:bg-white-3": !selected,
          "bg-blue-100 hover:bg-blue-200": selected,
        }
      )}
      onClick={onSelected}
      ref={ref}
    >
      <div className="flex flex-1">
        {previews.length > 0 ? (
          <>
            {previews.map((p, i) => (
              <span className="flex" key={i}>
                <svg width={p.width} height={p.height}>
                  <path d={p.data} />
                </svg>
                {i < previews.length - 1 && <span className="mx-1">/</span>}
              </span>
            ))}
          </>
        ) : (
          labels
        )}
      </div>

      {selected && (
        <div className="absolute inset-y-0 right-3 flex items-center justify-center text-aqua-500">
          <CheckmarkLarge className="h-6 w-6" />
        </div>
      )}
    </li>
  )
}

interface SelectProps {
  id: string
  open: boolean
  setOpen: React.Dispatch<React.SetStateAction<boolean | undefined>>
  selected: string
  selectedLabel: (value: string) => React.ReactNode
  label: string
  children: React.ReactNode
  adoptHref: string
  optionsCount: number
}

export const Select = ({
  id,
  open,
  setOpen,
  selected,
  selectedLabel,
  label,
  children,
  adoptHref,
  optionsCount,
}: SelectProps) => {
  const { t } = useTranslation()
  const desktop = useMedia(`(min-width: 1024px)`, true)
  const buttonRef = useRef<HTMLButtonElement>(null)

  const handleOpen = (open: boolean) => {
    setOpen(open)

    if (!open) {
      buttonRef.current?.focus()
    }
  }

  const toggle = () => handleOpen(!open)

  const { x, y, strategy, refs, context } = useFloating({
    open,
    onOpenChange: desktop ? handleOpen : undefined,
    middleware: desktop
      ? [
          offset(2),
          size({
            apply({ availableWidth, elements, rects }: SizeApplyArgs) {
              Object.assign(elements.floating.style, {
                maxWidth: `${availableWidth}px`,
                width: `${rects.reference.width}px`,
              })
            },
          }),
        ]
      : [],
  })

  const dismiss = useDismiss(context)

  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss])

  const ssrDisabled = useSSRDisabled()

  const dropdownContents = (
    <>
      {children}
      <div className="m-4 flex flex-col items-start border-t border-white-10 bg-white pb-4 pt-4">
        <h5 className="heading-five">{t("examples.more_fonts")}</h5>
        <p className="paragraph-sm advanced_customisation mb-0">
          <Trans i18nKey="examples.advanced_customisation" components={{ a: <a href={adoptHref}>link</a> }} />
        </p>
      </div>
    </>
  )

  // mobile
  const mt = 144
  const availableHeight = window.innerHeight - mt
  const headerAndFooter = 270
  const optionsHeight = optionsCount * 48
  const dropdownHeight = optionsHeight + headerAndFooter

  const scrollable = desktop ? optionsCount > 6 : availableHeight < dropdownHeight

  return (
    <div className="relative">
      <label htmlFor={id} className="w-full">
        {label && <div className="mb-1 text-sm font-semibold">{label}</div>}
        <div className="relative w-full">
          <button
            onClick={toggle}
            id={id}
            aria-invalid="false"
            ref={mergeRefs(buttonRef, refs.setReference)}
            type="button"
            disabled={ssrDisabled}
            className={cn(
              "font-dropdown inline-flex h-12 w-full items-center truncate rounded border pl-4 pr-12 text-base outline-none transition placeholder:text-[rgb(117,117,117)] focus:border-primary focus:ring-2 focus:ring-primary focus:ring-opacity-50 lg:h-10",
              {
                "hover:border-ink-50": !open,
                "cursor-not-allowed bg-gray-50": ssrDisabled,
              }
            )}
            {...getReferenceProps()}
          >
            <>
              <div className="w-full truncate">
                {selectedLabel ? selectedLabel(selected) : <>{typeof selected === "string" ? selected : ""}</>}
              </div>
              <div className="absolute inset-y-0 right-3 flex items-center justify-center text-ink-100">
                <ChevronDown className={cn("h-6 w-6 transition-transform", { "rotate-180": open })} />
              </div>
            </>
          </button>
        </div>
      </label>

      <FloatingPortal>
        {desktop ? (
          open && (
            <div
              className="z-20 flex w-full origin-top flex-col rounded-lg border border-white-10 bg-white shadow-custom-sm focus:outline-none lg:min-h-[84px]"
              ref={refs.setFloating}
              style={{
                position: strategy,
                top: y ?? 0,
                left: x ?? 0,
                width: "max-content",
              }}
              {...getFloatingProps()}
            >
              {scrollable ? (
                <div className="visible-scrollbar mt-2 h-96 flex-grow border-white-10">
                  <Scrollable>{dropdownContents}</Scrollable>
                </div>
              ) : (
                <div className="mt-2 border-white-10">{dropdownContents}</div>
              )}
            </div>
          )
        ) : (
          <Transition show={open}>
            <Transition.Child
              as={React.Fragment}
              enter="ease-out duration-150"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="ease-in duration-150"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <div className="fixed inset-0 z-40 bg-black bg-opacity-25" />
            </Transition.Child>

            <div className="fixed inset-0 z-40 overflow-y-auto">
              <div className="flex min-h-full items-start justify-center px-3 py-4 text-center sm:items-center sm:p-0">
                <Transition.Child
                  as={React.Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0 translate-y-10"
                  enterTo="opacity-100 translate-y-0"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100 translate-y-0"
                  leaveTo="opacity-0 translate-y-10"
                >
                  <div
                    className={cn(
                      "minimatic-mobile-dropdown fixed inset-x-0 bottom-0 z-50 mt-36 flex transform flex-col rounded-t-2xl bg-white shadow-custom-sm ring-0 transition-all focus:outline-none",
                      {
                        "top-0": scrollable,
                      }
                    )}
                  >
                    <div className="flex flex-shrink-0 flex-grow-0 items-center justify-between gap-3 p-4">
                      <h4 className="heading-four mb-0">{t("examples.valj_typsnitt")}</h4>
                      <button
                        onClick={toggle}
                        className="flex h-12 w-12 flex-shrink-0 items-center justify-center"
                        type="button"
                      >
                        <CloseLarge className="h-6 w-6" />
                      </button>
                    </div>
                    {scrollable ? (
                      <div className="visible-scrollbar mt-2 flex-grow border-t border-white-10">
                        <Scrollable>{dropdownContents}</Scrollable>
                      </div>
                    ) : (
                      <div className="mt-2 border-t border-white-10">{dropdownContents}</div>
                    )}
                    <button
                      type="button"
                      className="button button-primary button-medium m-4 !ml-4 mt-2"
                      onClick={toggle}
                    >
                      <CheckmarkLarge />
                      <span>{t("toolbox.done")}</span>
                    </button>
                  </div>
                </Transition.Child>
              </div>
            </div>
          </Transition>
        )}
      </FloatingPortal>
    </div>
  )
}

export interface MinimaticFontsConfiguration {
  fonts?: string[]
  fontsEnabled?: boolean
}

export type MinimaticFontsSign = Pick<ExampleSign, "fonts">

interface MinimaticFontsProps {
  minimatic?: Sign
  sign: MinimaticFontsSign
  config?: MinimaticFontsConfiguration | null
  loadingFailed?: boolean
  setFont: (font: string) => void
  adoptHref: string
}

export const MinimaticFonts: React.FC<MinimaticFontsProps> = ({
  minimatic,
  sign,
  config,
  loadingFailed,
  setFont,
  adoptHref,
}) => {
  const loaded = useMinimaticLoaded(minimatic)

  if (config && !loadingFailed && (!minimatic || !loaded) && (config.fonts || []).length >= 1) {
    return <FontPickerSkeleton />
  }

  if (!minimatic || !loaded || !config) {
    return null
  }

  return <FontPicker minimatic={minimatic} sign={sign} config={config} setFont={setFont} adoptHref={adoptHref} />
}

const FontPickerSkeleton: React.FC = () => {
  const { t } = useTranslation()

  return (
    <div className="mb-4">
      <div className="relative">
        <label className="w-full">
          <div className="mb-1 text-sm font-semibold">{t("toolbox.font")}</div>
          <div className="relative h-10 w-full overflow-hidden rounded">
            <div className="skeleton skeleton-rounded h-10 w-full"></div>
          </div>
        </label>
      </div>
    </div>
  )
}

type handlerFunc = (
  minimatic: Sign
) => [handler: (data: SignStateData) => void, current: string | undefined, fonts: string[]]

const useFontHandler: handlerFunc = minimatic => {
  const [availableFonts, setFonts] = useState<string[]>([])
  const [current, setCurrent] = useState<string>()

  const handler = useCallback(
    ({ state, data }: SignStateData) => {
      const signFonts = arrayUniq(state.objects.filter(isText).map(t => t.font))

      setCurrent(signFonts.join(fontSeparator))

      const currentData = data.find(data => data.name === minimatic.signType)

      if (currentData) {
        setFonts(Fonts.enabledFonts(minimatic.signType || "", minimatic.fontdata, minimatic.data.font_set || ""))
      }
    },
    [minimatic.signType, minimatic.fontdata, minimatic.data.font_set]
  )

  return [handler, current, availableFonts]
}

interface FontPickerProps {
  minimatic: Sign
  sign: MinimaticFontsSign
  config: MinimaticFontsConfiguration
  setFont: (font: string) => void
  adoptHref: string
}

const FontPicker: React.FC<FontPickerProps> = ({ minimatic, sign, config, setFont, adoptHref }) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [scrolledIntoView, setScrolledIntoView] = useState(false)
  const [initFontArray, setInitFontArray] = useState<string[]>()
  const [initFontSizeArray, setInitFontSizeArray] = useState<number[]>()
  const [handler, current] = useFontHandler(minimatic)
  useMinimaticData(handler, minimatic)
  const texts = minimatic.allObjects.filter(obj => typeof obj.state.text === "string") as SignText[]
  const initFont = arrayUniq(initFontArray).join(fontSeparator)
  const currentFont = arrayUniq(texts.map(t => t.fontFamily)).join(fontSeparator)

  useEffect(() => {
    if (!initFontArray) {
      setInitFontArray(texts.map(t => t.fontFamily || "").filter(t => t !== ""))
      setInitFontSizeArray(texts.map(t => t.state.size))
    }
  }, [initFontArray, texts])

  const handleOpen = (open: boolean) => {
    setOpen(open)
    setScrolledIntoView(false)
  }

  const fontOptions = [initFont || "", ...(config.fonts || [])]

  const onChange = (font: string) => {
    texts.forEach((text, i) => {
      // if initial sign has multiple fonts, first/default option is an array
      const textFont = font === initFont && initFontArray ? initFontArray[i] : font

      minimatic.updateExactObjectState(text, { font: textFont })

      // keep within bounds
      if (initFontSizeArray) {
        minimatic.setObjectActive(text.state)
        minimatic.minimaticText(text.state.text, initFontSizeArray[i])
        minimatic.setObjectActive(null)
      }
    })

    setFont(font)
  }

  if (!config.fontsEnabled || (config.fonts || []).length < 1) {
    return null
  }

  return (
    <div className="mb-4">
      <Select
        id="example-font-select"
        label={t("toolbox.font")}
        open={open}
        setOpen={handleOpen}
        selected={current || (sign.fonts || [])[0] || ""}
        selectedLabel={v => (
          <div className="flex w-full items-center justify-between">
            <span>{v}</span>
            <div className="flex items-center gap-2"></div>
          </div>
        )}
        adoptHref={adoptHref}
        optionsCount={fontOptions.length}
      >
        {fontOptions.map((fontOpt, i) => {
          return (
            <Option
              key={i}
              label={fontOpt}
              selected={fontOpt === currentFont}
              onSelected={() => onChange(fontOpt)}
              scrollIntoView={open && fontOpt === currentFont && !scrolledIntoView}
              setScrolledIntoView={setScrolledIntoView}
            ></Option>
          )
        })}
      </Select>
    </div>
  )
}

interface MinimaticFontsConfigurerProps {
  minimatic: Sign
  config?: MinimaticFontsConfiguration
  update: (config: Partial<MinimaticFontsConfiguration>) => void
}

export const MinimaticFontsConfigurer: React.FC<MinimaticFontsConfigurerProps> = ({ minimatic, config, update }) => {
  const loaded = useMinimaticLoaded(minimatic)
  const [handler, current, availableFonts] = useFontHandler(minimatic)
  useMinimaticData(handler, minimatic)
  const [availableFontsSelected, setAvailableFontsSelected] = useState<string[]>([])
  const [activeFontsSelected, setActiveFontsSelected] = useState<string[]>([])
  const activeFonts = config?.fonts || []
  const active = typeof config?.fontsEnabled !== "undefined" ? config.fontsEnabled : true

  if (!current || !loaded) {
    return null
  }

  const toggleFont = (font: string) => {
    setAvailableFontsSelected(selected => {
      if (selected.find(f => f === font)) {
        return selected.filter(f => f !== font)
      } else {
        return [...selected, font]
      }
    })
  }

  const toggleActiveFont = (font: string) => {
    setActiveFontsSelected(selected => {
      if (selected.find(f => f === font)) {
        return selected.filter(f => f !== font)
      } else {
        return [...selected, font]
      }
    })
  }

  const moveToActive = () => {
    setAvailableFontsSelected([])
    update({ fonts: [...activeFonts, ...availableFontsSelected] })
  }

  const moveToAvailable = () => {
    setActiveFontsSelected([])
    update({ fonts: activeFonts.filter(f => !activeFontsSelected.find(a => a === f)) })
  }

  return (
    <div className="minimatic-fonts-config grid grid-cols-[1fr_min-content_1fr]" data-title="fonts">
      <div className="mb-3 flex items-center gap-2">
        <h4 className="mb-0 w-auto whitespace-nowrap text-xl leading-6">All fonts</h4>
        <p className="paragraph-sm mb-0 leading-6">Aim for 5-12 fonts</p>
      </div>
      <div></div>
      <div className="mb-3 flex items-center gap-3 [&_.checkbox-label]:!mb-0 [&_.checkbox]:mb-0 [&_.checkbox]:py-0">
        <h4 className="mb-0 w-auto whitespace-nowrap text-xl leading-6">Active fonts</h4>
        <Checkbox
          name="toggle_fonts"
          checked={active}
          label="Activate"
          onChange={e => update({ fontsEnabled: e.target.checked })}
        />
      </div>
      <div className="minimatic-fonts-available h-72 rounded-lg border border-white-20 py-4">
        <FontList
          fonts={availableFonts.filter(f => !activeFonts.includes(f) && f !== current)}
          active={availableFontsSelected}
          onSelect={toggleFont}
        />
      </div>
      <div className="flex items-center">
        <div className="space-y-2 p-1">
          <div>
            <button
              className="minimatic-activate-fonts button button-secondary icon-only mb-0"
              type="button"
              disabled={availableFontsSelected.length === 0}
              onClick={moveToActive}
            >
              <ArrowRightLarge className="h-6 w-6" />
            </button>
          </div>
          <div>
            <button
              className="minimatic-deactivate-fonts button button-secondary icon-only mb-0"
              type="button"
              disabled={activeFontsSelected.length === 0}
              onClick={moveToAvailable}
            >
              <ArrowLeftLarge className="h-6 w-6" />
            </button>
          </div>
        </div>
      </div>
      <div className="minimatic-fonts-selected h-72 rounded-lg border border-white-20 p-4">
        <FontList
          fonts={[current, ...activeFonts]}
          active={activeFontsSelected}
          locked={current}
          onSelect={toggleActiveFont}
        />
      </div>
    </div>
  )
}

interface FontListProps {
  fonts: string[]
  active: Array<string | undefined>
  locked?: string
  onSelect: (fonts: string) => void
}

const FontList = ({ fonts, active, locked, onSelect }: FontListProps) => {
  return (
    <div className="flex h-full flex-wrap gap-2">
      <Scrollable>
        {fonts.map(font => {
          const lockedFont = locked == font
          const labels = font.split(fontSeparator)
          const previews: FontPreview[] = []

          labels.forEach(l => {
            let preview = fontPreviews[l]

            if (!preview) preview = fontPreviews[l] = computePreview(l)

            if (preview) previews.push(preview)
          })

          return (
            <div
              key={font.toString()}
              className={cn(
                "minimatic-font relative flex h-10 cursor-pointer items-center rounded transition",
                `font-${font}`,
                {
                  "mx-3 pl-4": !locked,
                  "bg-blue-100 hover:bg-blue-200": active.includes(font),
                  "hover:bg-white-3": !active.includes(font),
                  "pointer-events-none opacity-90": lockedFont,
                }
              )}
              onClick={() => onSelect(font)}
            >
              {locked && (
                <div className="w-6">
                  {lockedFont && <LockClosedFill className="h-6 w-6 [&_path]:stroke-white-3 [&_path]:stroke-1" />}
                </div>
              )}

              {previews.length > 0 ? (
                <>
                  {previews.map((p, i) => (
                    <span className="flex" key={i}>
                      <svg key={p.data} width={p.width} height={p.height}>
                        <path d={p.data} />
                      </svg>
                      {i < previews.length - 1 && <span className="mx-1 mt-1">/</span>}
                    </span>
                  ))}
                </>
              ) : (
                labels
              )}
            </div>
          )
        })}
      </Scrollable>
    </div>
  )
}
